// #336 slice 6, brief §7.3: the text of one cell of the wide editor's grid,
// one exercise in one round read through one lens (mock `cellTxt`). The
// values come from readRound and the reference from roundReferenceKg; the
// numbers are spelled by the Draft line formatter. Keep free of React and
// any UI import.
import { formatDraftNumber, formatDraftValue, type DraftLineOptions } from "./previewFormat.js";
import type { SlotDraft } from "./programEdit.js";
import { setCountAt } from "./programEditRoundSets.js";
import { slotRoundPeak } from "./programIntensityDraft.js";
import { isEffortAxis, readRound, type ReadSet } from "./programProgression.js";
import { referenceLoadable, roundReferenceKg } from "./programProgressionReference.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";

/** What a grid cell shows: the effort, the reps, the load, or the set count
 *  of that round (brief §15 row 36). */
export type DraftGridLens = "effort" | "reps" | "load" | "sets";

/** A value nobody has typed yet, or one that does not parse. */
const UNSET = "?";
/** The kilos of a row in kilos are not written yet (the MAX is not the
 *  question, so not NO_LOAD's «?»). */
export const LOAD_UNWRITTEN = "…";
/** A row with no load axis at all, in the effort lens. */
const NO_AXIS = "–";
/** No load to show: no kilos written and no MAX to read one from. */
export const NO_LOAD = "?";
/** The exercise does not run in this round (the cell has no text at all). */
export const NO_ROUND = "–";

/** A load in the cell's unit as a bare number: the column says the unit. */
const bareLoad = (value: number, o: DraftLineOptions): string =>
  formatDraftValue("absolute", value, { ...o, labels: { ...o.labels, units: { kg: "", lb: "" } } }).trim();

const num = (n: number | null): string => (n === null ? UNSET : formatDraftNumber(n));

// One set's reps with the "+" of an AMRAP set on that set alone.
const repsOf = (s: ReadSet): string => `${num(s.reps)}${s.amrap ? "+" : ""}`;

// «4×5», «3×40s», «3×5/40s», «3×5/5/3+»: the set count, then the reps when
// they agree or each set's when they do not. A timed row with reps types them
// before the clock, as the preview does («5 / 40 s»), without the spaces.
const repsText = (
  sets: readonly ReadSet[],
  timed: boolean,
  o: DraftLineOptions,
  repsSets: readonly ReadSet[] | null,
): string => {
  const n = sets.length;
  if (timed && repsSets && repsSets.some((r) => r.reps !== null)) {
    // One duration or an interval «20-40s»; a zero-width space after the bar is
    // the one clean place to wrap a narrow cell.
    const reps = [...new Set(repsSets.map((r) => (r.reps === null ? "" : repsOf(r))))];
    const secs = sets.map((s) => s.reps).filter((v): v is number => v !== null);
    const lo = secs.length ? Math.min(...secs) : null;
    const hi = secs.length ? Math.max(...secs) : null;
    const clock = lo === hi ? num(lo) : `${num(lo)}-${num(hi)}`;
    const lead = reps.every((r) => r === "") ? "" : `${reps.join(",")}/\u200B`;
    return `${n}×${lead}${clock}${o.labels.seconds}`;
  }
  const reps = sets.map(repsOf);
  const same = reps.every((r) => r === reps[0]);
  const body = same ? reps[0]! : reps.join("/");
  return `${n}×${body}${timed ? o.labels.seconds : ""}`;
};

// «8», «7→9», «r2» for RIR, «+» when a set is an AMRAP. A row on another axis
// says which one, so the cell is never read as empty. A row in kilos with a
// MAX says the load of the set that gives the band, and its % of the MAX.
const effortText = (
  slot: SlotDraft,
  w: number,
  sets: readonly ReadSet[],
  timed: boolean,
  o: DraftLineOptions,
  max: MaxForProposal | undefined,
): string => {
  const effort = sets.filter((s) => isEffortAxis(s.kind) && s.value !== null);
  if (effort.length === 0) {
    const kind = sets[0]!.kind;
    if (kind === "absolute") {
      const peak = slotRoundPeak(slot, w, { max, cellUnit: o.cellUnit });
      const kg = peak?.set.kind === "absolute" ? peak.set.value : null;
      if (kg !== null && o.labels.ofMax) return o.labels.ofMax(formatDraftValue("absolute", kg, o), peak!.value);
      return o.labels.units[o.displayUnit];
    }
    if (kind === "percent-of-max") return "%";
    if (kind === "history") return o.labels.history;
    // A row at RPE/RIR with nothing typed yet is unset, not "no axis".
    if (isEffortAxis(kind)) return UNSET;
    return timed ? o.labels.seconds : NO_AXIS;
  }
  // «r2→@8»: with RIR and RPE in the one range the prefix is per value, «r»
  // for RIR and «@» for RPE. On one axis it stays as it was: «r2→0», «7→9».
  const mixed = new Set(effort.map((s) => s.kind)).size > 1;
  const mark = (s: ReadSet): string => {
    const v = num(s.value);
    if (!mixed) return v;
    return s.kind === "rir" ? `r${v}` : `@${v}`;
  };
  const values = [...new Set(effort.map(mark))];
  const span = values.length === 1 ? values[0]! : `${values[0]!}→${values[values.length - 1]!}`;
  const lead = !mixed && effort[0]!.kind === "rir" ? "r" : "";
  return `${lead}${span}${sets.some((s) => s.amrap) ? "+" : ""}`;
};

// The kilos written on the first set, or «≈X» from the MAX for a row at
// effort or at %, as the round ladder's «ref. ≈ X».
const loadText = (
  slot: SlotDraft,
  w: number,
  sets: readonly ReadSet[],
  o: DraftLineOptions,
  max: MaxForProposal | undefined,
): string => {
  const first = sets[0]!;
  if (first.kind === "absolute") return first.value === null ? LOAD_UNWRITTEN : bareLoad(first.value, o);
  const kg = roundReferenceKg(slot, w, max);
  return kg === null ? NO_LOAD : `≈${bareLoad(referenceLoadable(kg, o.cellUnit), o)}`;
};

/** One exercise in round `w` through `lens`. Null when it does not run in
 *  that round. `max`: the athlete's MAX for the exercise, for the load lens
 *  and for a row in kilos in the effort lens. */
export const formatDraftGridCell = (
  slot: SlotDraft,
  w: number,
  lens: DraftGridLens,
  o: DraftLineOptions,
  max?: MaxForProposal,
): string | null => {
  const timed = slot.setRows[0]?.workKind === "countdown";
  const sets = readRound(slot, w, timed);
  if (sets === null || sets.length === 0) return null;
  if (lens === "sets") return String(setCountAt(slot, w));
  if (lens === "reps") return repsText(sets, timed, o, timed ? readRound(slot, w, false) : null);
  if (lens === "effort") return effortText(slot, w, sets, timed, o, max);
  return loadText(slot, w, sets, o, max);
};
